import React, { useEffect } from 'react'
import { PublicMeeting } from '@chilipiper/api-client/src/meetings'
import { Box, Flex, Spinner, Text } from '@chilipiper/design-system'
import { AlertBanner, LinkButton, Section } from '@chilipiper/design-system/src/new'
import { useTranslations } from '../../../context/TranslationContext'
import { chatPublicEventAction, chiliChatEventName } from '../../../constants'
import { DisplayDate } from './DisplayDate'

type Props = {
  email: string | undefined
  meeting?: PublicMeeting
  onClick: () => void
  timezone: string
  enableReschedule: boolean
}

export const BookingConfirmation = ({
  email,
  meeting,
  onClick,
  timezone,
  enableReschedule,
}: Props) => {
  const [
    youWillReceiveMeetingInvitationShortly,
    time,
    guests,
    rescheduleButton,
    loadingMeetingDetails,
  ] = useTranslations([
    'youWillReceiveMeetingInvitationShortly',
    'time',
    'guests',
    'rescheduleButton',
    'loadingMeetingDetails',
  ])
  useEffect(() => {
    if (meeting) {
      window.dispatchEvent(
        new CustomEvent(chiliChatEventName, {
          detail: {
            action: chatPublicEventAction.BOOKING_CONFIRMED,
            args: { meetingId: meeting.meetingId },
          },
        })
      )
    }
  }, [meeting])
  if (!meeting) {
    return (
      <Flex justifyContent='center' alignItems='center' h='132px'>
        <Spinner mr={2} />
        <Text color='fg/moderate' textStyle='body-multiline'>
          {loadingMeetingDetails ?? 'Loading meeting details'}
        </Text>
      </Flex>
    )
  }
  return (
    <Box px={3} pt={2}>
      <Section>
        <AlertBanner
          variant='neutral'
          title={
            youWillReceiveMeetingInvitationShortly ??
            'You will receive a meeting invitation shortly'
          }
        />
      </Section>

      <Flex pt={6} pb={5} mx={2} boxShadow='border/row'>
        <Box w={12} flexShrink={0}>
          <Text textStyle='body-multiline' color='fg/moderate'>
            {time}
          </Text>
        </Box>
        <Box>
          <Text color='fg/major' textStyle='body-multiline'>
            <DisplayDate timezone={timezone} date={meeting.dateTime} />
          </Text>
          {enableReschedule && (
            <LinkButton mt={3} data-test-id='reschedule-button' variant='minor' onClick={onClick}>
              {rescheduleButton ?? 'Reschedule'}
            </LinkButton>
          )}
        </Box>
      </Flex>

      <Flex pt={4} pb={5} px={2}>
        <Box w={12} flexShrink={0}>
          <Text textStyle='body-multiline' color='fg/moderate'>
            {guests}
          </Text>
        </Box>
        <Text textStyle='body-multiline-tight' color='fg/major' wordBreak='break-all'>
          {email ?? 'You'}
        </Text>
      </Flex>
    </Box>
  )
}
